/**
 * API Client Constants
 * 
 * Conventions:
 * - All magic numbers extracted
 * - Self-documenting names
 * - Easy to adjust without hunting through code
 * - Type-safe constant objects
 */

// ============================================================================
// Environment
// ============================================================================

// Gate diagnostic logging on VITE_ENVIRONMENT, NOT import.meta.env.DEV —
// staging is a production-mode Vite build, so DEV is false exactly where QA
// debugs. This is verbose in dev + staging, silent only in production.
export const IS_PRODUCTION = import.meta.env.VITE_ENVIRONMENT === 'production';

// ============================================================================
// API Configuration
// ============================================================================

/**
 * API client configuration
 */
export const API_CONFIG = {
  /**
   * Base URL for API requests
   * From environment variable
   */
  get baseUrl(): string {
    return import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/public';
  },
  
  /**
   * API version prefix
   * Prepended to all endpoint paths
   */
  version: '/v1',
  
  /**
   * Default request timeout in milliseconds
   * Most requests should complete within this time
   */
  DEFAULT_TIMEOUT_MS: 30_000, // 30 seconds
  
  /**
   * Short timeout for health checks and quick operations
   */
  SHORT_TIMEOUT_MS: 5_000, // 5 seconds
  
  /**
   * Long timeout for file uploads and heavy operations
   */
  LONG_TIMEOUT_MS: 120_000, // 2 minutes
} as const;

// ============================================================================
// Retry Configuration
// ============================================================================

/**
 * Retry logic configuration
 * 
 * Uses exponential backoff with jitter to prevent thundering herd.
 */
export const RETRY_CONFIG = {
  /**
   * Maximum number of retry attempts for failed requests
   */
  MAX_RETRIES: 3,
  
  /**
   * Initial delay before first retry (ms)
   * 
   * Each subsequent retry multiplies this by BACKOFF_MULTIPLIER:
   * - Attempt 1: 1s
   * - Attempt 2: 2s
   * - Attempt 3: 4s
   */
  INITIAL_DELAY_MS: 1_000, // 1 second
  
  /**
   * Maximum delay between retries (ms)
   * Caps the exponential backoff
   */
  MAX_DELAY_MS: 10_000, // 10 seconds
  
  /**
   * Exponential backoff multiplier
   * Delay = INITIAL_DELAY_MS * (BACKOFF_MULTIPLIER ^ attempt)
   */
  BACKOFF_MULTIPLIER: 2,
  
  /**
   * HTTP status codes that should trigger a retry
   * 
   * - 408: Request Timeout
   * - 500: Internal Server Error (transient)
   * - 502: Bad Gateway (upstream issue)
   * - 503: Service Unavailable (temporary)
   * - 504: Gateway Timeout (upstream timeout)
   *
   * 429 is deliberately NOT retryable: a rate limiter refused the request,
   * and retrying burns the caller's remaining budget while delaying the
   * throttle message the response body carries (the API's limiter 429s are
   * envelope-shaped; `result.message` is user copy). In THIS client an
   * enveloped 429 was already returned rather than retried (HTTP errors with
   * a parseable body never reach the retry predicate) — removing 429 here
   * closes the non-JSON-body path (proxy/Fly error pages) and keeps the three
   * clients' policy identical; the real 4x retry loop was admin-portal's.
   * Provider outages keep surfacing as 503, which stays retryable.
   */
  RETRYABLE_STATUS_CODES: [408, 500, 502, 503, 504] as const,
} as const;

// ============================================================================
// Token Configuration (for Auth Service)
// ============================================================================

/**
 * Token management configuration
 * 
 * **NOTE:** Token functions are currently stubbed.
 * Will be implemented by Auth Service.
 */
export const TOKEN_CONFIG = {
  /**
   * How long before token expiry to trigger refresh (ms)
   * 
   * If token expires in less than this time, trigger refresh.
   */
  REFRESH_THRESHOLD_MS: 60_000, // 1 minute before expiry
  
  /**
   * Storage keys for tokens
   * Used by Auth Service to store/retrieve tokens
   */
  ACCESS_TOKEN_KEY: 'auth.access_token',
  REFRESH_TOKEN_KEY: 'auth.refresh_token',
  TOKEN_EXPIRY_KEY: 'auth.token_expiry',
} as const;

// ============================================================================
// Request Deduplication Configuration
// ============================================================================

/**
 * Request deduplication configuration
 * 
 * Prevents duplicate API calls within a time window.
 * Useful for React's double-render in development.
 */
export const REQUEST_DEDUP_CONFIG = {
  /**
   * Time window for request deduplication (ms)
   * 
   * If an identical request is made within this window,
   * return the existing promise instead of making a new request.
   */
  DEDUP_WINDOW_MS: 1_000, // 1 second
  
  /**
   * Whether to enable request deduplication
   * 
   * Can be disabled globally for testing.
   */
  ENABLED: true,
} as const;

// ============================================================================
// Error Codes
// ============================================================================

/**
 * Error codes
 * 
 * Combines client-side errors with server errors from API_STANDARD.md.
 */
export const ERROR_CODES = {
  // ========================================
  // Client-side errors
  // ========================================
  
  /**
   * Network error (fetch failed)
   * 
   * Triggered when:
   * - No internet connection
   * - DNS resolution failed
   * - Connection refused
   */
  NETWORK_ERROR: 'NETWORK_ERROR',
  
  /**
   * Request timeout
   * 
   * Triggered when request takes longer than configured timeout.
   */
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  
  /**
   * Request cancelled
   * 
   * Triggered when request is manually aborted.
   */
  REQUEST_CANCELLED: 'REQUEST_CANCELLED',
  
  /**
   * JSON parse error
   * 
   * Server returned invalid JSON (e.g., HTML error page).
   * Retry behavior: Only retries if HTTP status is retryable (5xx/408; never 429).
   */
  PARSE_ERROR: 'PARSE_ERROR',
  
  // ========================================
  // Server errors (from API_STANDARD.md)
  // ========================================
  
  /**
   * Validation error (400)
   * 
   * User input failed validation.
   * Component should handle these with form validation.
   */
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  
  /**
   * Unauthorized (401)
   * 
   * Authentication required or invalid token.
   * Triggers redirect to login.
   */
  UNAUTHORIZED: 'UNAUTHORIZED',
  
  /**
   * Forbidden (403)
   * 
   * User lacks permission for this action.
   */
  FORBIDDEN: 'FORBIDDEN',
  
  /**
   * Not found (404)
   * 
   * Requested resource doesn't exist.
   */
  NOT_FOUND: 'NOT_FOUND',
  
  /**
   * Conflict (409)
   * 
   * Resource conflict (e.g., duplicate email).
   */
  CONFLICT: 'CONFLICT',
  
  /**
   * Business rule violation (400)
   * 
   * Business logic prevents this action.
   */
  BUSINESS_RULE_VIOLATION: 'BUSINESS_RULE_VIOLATION',
  
  /**
   * Internal server error (500)
   * 
   * Unexpected server error.
   */
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  
  /**
   * Database error (500)
   * 
   * Database operation failed.
   */
  DATABASE_ERROR: 'DATABASE_ERROR',
  
  /**
   * External service error (502)
   * 
   * Third-party service failed (e.g., Stripe API).
   */
  EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',
} as const;

/**
 * Error code type
 * 
 * Type-safe union of all error codes.
 */
export type ErrorCode = typeof ERROR_CODES[keyof typeof ERROR_CODES];

// ============================================================================
// HTTP Headers
// ============================================================================

/**
 * HTTP headers used for monitoring and multi-tenancy
 */
export const HEADERS = {
  /**
   * Client identification headers
   * 
   * Expected by @butter/monitoring for tracking and analytics.
   */
  CLIENT_APP: 'X-Client-App',
  CLIENT_PLATFORM: 'X-Client-Platform',
  
  /**
   * Multi-tenancy headers
   * 
   * Expected by API middleware for tenant isolation.
   */
  TENANT_ID: 'X-Tenant-Id', // Tenant handle (e.g., "drivingrangecollective")
  CLUB_ID: 'X-Club-Id',     // Club ID (e.g., 2)
  
  /**
   * Authentication header
   */
  AUTHORIZATION: 'Authorization',
  
  /**
   * Standard content type
   */
  CONTENT_TYPE: 'Content-Type',
} as const;

/**
 * Client identification values
 * 
 * Sent in X-Client-App and X-Client-Platform headers.
 */
export const CLIENT_INFO = {
  /**
   * Application name
   * Used for monitoring and analytics
   */
  APP: 'web-portal-2',
  
  /**
   * Platform identifier
   * Used for monitoring and analytics
   */
  PLATFORM: 'web',
} as const;
